import {
  Box,
  Button,
  Flex,
  FormLabel,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Text,
  useColorModeValue,
  useDisclosure,
  useBreakpointValue,
} from "@chakra-ui/react";
import AppSpinner from "components/spinner/AppSpinner";
import { IconArrowDown, IconPencil } from "components/icons/Icons";
import { AuthInput } from "components/auth/AuthInput";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  isChangingTeammemberRoleSelector,
  isSavingTeammemberSelector,
  isRemovingTeammemberSelector,
  isFetchingTeamUsersSelector,
  teamUsersSelector,
  userSelector,
} from "store/user/selector";
import {
  addTeammemberRequest,
  deleteTeammemberRequest,
  fetchTeamUsersRequest,
  updateTeammemberRoleRequest,
} from "store/user/reducer";
import { AddIcon } from "@chakra-ui/icons";
import deleteIcon from "assets/img/customer/delete-icon.svg";

export default function Collaborators() {
  const teamUsers = useSelector(teamUsersSelector);
  const user = useSelector(userSelector);
  const dispatch = useDispatch();
  const isSavingTeammember = useSelector(isSavingTeammemberSelector);
  const isRemovingTeammember = useSelector(isRemovingTeammemberSelector);
  const isChangingTeammemberRole = useSelector(
    isChangingTeammemberRoleSelector,
  );
  const isFetchingTeamUsers = useSelector(isFetchingTeamUsersSelector);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const [memberToDelete, setMemberToDelete] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const shadow = useColorModeValue(
    "0px 4px 50px rgba(0, 0, 0, 0.1)",
    "0px 4px 50px rgba(0, 0, 0, 0.1)",
  );

  const members = useMemo(() => {
    return (teamUsers || [])
      .slice()
      .sort((a: any, b: any) => (b.team_users_id || 0) - (a.team_users_id || 0))
      .map((it: any) => {
        const firstName = String(it?.first_name || "").trim();
        const lastName = String(it?.last_name || "").trim();

        const name = `${lastName} ${firstName}`.trim() || it.email || "Unknown";

        const roleKey: "admin" | "standard" =
          String(it.role) === "0" ? "admin" : "standard";
        const role = roleKey === "admin" ? "Admin" : "User";
        const status = (it.status || "active").toLowerCase();
        const billingAccess = Number(it.billing_access) === 1;

        return {
          id: it.team_users_id,
          name,
          firstName,
          lastName,
          email: it.email,
          role,
          roleKey,
          rawRole: it.role,
          status,
          billingAccess,
        };
      });
  }, [teamUsers]);

  const isMobile = useBreakpointValue({ base: true, md: false });

  const [inviteFirstName, setInviteFirstName] = useState("");
  const [inviteLastName, setInviteLastName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "standard">("admin");

  const wasSavingTeammemberRef = useRef(false);
  const wasChangingRoleRef = useRef(false);
  const wasRemovingTeammemberRef = useRef(false);

  const resetInviteForm = () => {
    setInviteFirstName("");
    setInviteLastName("");
    setInviteEmail("");
    setInviteRole("admin");
  };

  const onOpenInvite = () => {
    resetInviteForm();
    onOpen();
  };

  const [removingMemberId, setRemovingMemberId] = useState<number | null>(null);

  const [editMember, setEditMember] = useState<any | null>(null);
  const [editRole, setEditRole] = useState<"admin" | "standard">("admin");

  useEffect(() => {
    if (!isRemovingTeammember) {
      setRemovingMemberId(null);
    }
  }, [isRemovingTeammember]);

  useEffect(() => {
    if (isSavingTeammember) {
      wasSavingTeammemberRef.current = true;
      return;
    }

    if (wasSavingTeammemberRef.current) {
      wasSavingTeammemberRef.current = false;
      onClose();
      resetInviteForm();
    }
  }, [isSavingTeammember, onClose]);

  useEffect(() => {
    if (isChangingTeammemberRole) {
      wasChangingRoleRef.current = true;
      return;
    }

    if (wasChangingRoleRef.current) {
      wasChangingRoleRef.current = false;

      if (user?.user_id) {
        dispatch(fetchTeamUsersRequest({ user_id: user.user_id }));
      }

      onEditClose();
    }
  }, [dispatch, isChangingTeammemberRole, onEditClose, user?.user_id]);

  const onSubmitInvite = () => {
    const first_name = String(inviteFirstName || "").trim();
    const last_name = String(inviteLastName || "").trim();
    const name = `${last_name} ${first_name}`.trim();

    const payload = {
      business_id: user?.business_id,
      details: {
        name,
        first_name,
        last_name,
        email: inviteEmail,
        role: inviteRole,
      },
    };
    dispatch(addTeammemberRequest(payload));
  };

  const onRemoveMember = (member: { id: number; name: string }) => {
    setMemberToDelete(member);
    onDeleteOpen();
  };

  const onConfirmDelete = () => {
    if (!memberToDelete) return;
    setRemovingMemberId(memberToDelete.id);
    dispatch(deleteTeammemberRequest({ team_user_id: memberToDelete.id }));
  };

  useEffect(() => {
    if (isRemovingTeammember) {
      wasRemovingTeammemberRef.current = true;
      return;
    }

    if (wasRemovingTeammemberRef.current) {
      wasRemovingTeammemberRef.current = false;
      onDeleteClose();
      setMemberToDelete(null);
    }
  }, [isRemovingTeammember, onDeleteClose]);

  const onChangeRole = (team_user_id: number, role: "admin" | "standard") => {
    dispatch(updateTeammemberRoleRequest({ team_user_id, role }));
  };

  const onOpenEdit = (member: any) => {
    setEditMember(member);
    setEditRole(member?.roleKey === "admin" ? "admin" : "standard");
    onEditOpen();
  };

  const onSubmitEdit = () => {
    if (!editMember?.id) return;
    onChangeRole(editMember.id, editRole);
  };

  return (
    <Box>
      <Button
        leftIcon={<AddIcon w="10px" h="10px" />}
        bgColor="#3A7BFF"
        color="white"
        fontSize="14px"
        fontWeight="600"
        borderRadius="24px"
        padding="12px 20px"
        h={{ base: "40px", md: "32px" }}
        w={{ base: "full", md: "auto" }}
        border="1px solid #2C63E5"
        _hover={{
          bgColor: "#FFFFFF",
          color: "#2C63E5",
        }}
        _active={{
          bgColor: "#FFFFFF",
          color: "#2C63E5",
        }}
        onClick={onOpenInvite}
        mb="24px"
        isDisabled={teamUsers?.length > 9}
      >
        Invite new team member
      </Button>

      <Box
        p={{ md: "24px", base: "20px" }}
        border="1px solid #E5E7EB"
        borderRadius="12px"
        bg="white"
        w="full"
        maxW="1048px"
        position="relative"
      >
        {isFetchingTeamUsers && (
          <Flex
            position="absolute"
            inset="0"
            bg="rgba(255, 255, 255, 0.6)"
            zIndex="1"
            alignItems="center"
            justifyContent="center"
            borderRadius="12px"
          >
            <AppSpinner w="28px" h="28px" />
          </Flex>
        )}
        <Text fontSize="16px" fontWeight="600" color="#0B0E10" mb="4px">
          Team members
        </Text>
        <Text fontSize="14px" color="#0B0E10" mb="16px">
          Invite colleagues to help manage translation orders, view billing, or
          track progress. Each member’s access level determines what they can
          see and do.
        </Text>

        <Box border="1px solid #E5E7EB" borderRadius="12px" overflow="hidden">
          {!isMobile && (
            <Flex
              bg="#fff"
              borderBottom="1px solid #E5E7EB"
              px="16px"
              py="12px"
              fontSize="14px"
              fontWeight="600"
              color="#0B0E10"
            >
              <Box flex="2">Name</Box>
              <Box flex="1">Role</Box>
              <Box flex="3">Email</Box>
              <Box flex="1">Status</Box>
              <Box flex="1">Billing access</Box>
              <Box flex="1" pl="8px">
                Actions
              </Box>
            </Flex>
          )}

          {isMobile ? (
            <Box p="12px">
              {members.map((m: any) => (
                <Box
                  key={m.id}
                  border="1px solid #E5E7EB"
                  borderRadius="12px"
                  p="12px"
                  mb="12px"
                >
                  <Flex
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap="12px"
                  >
                    <Box flex="1">
                      <Text
                        fontSize="14px"
                        fontWeight="600"
                        color="#0B0E10"
                        noOfLines={1}
                        wordBreak="break-all"
                      >
                        {m.name}
                      </Text>
                      <Text fontSize="13px" color="#0B0E10" noOfLines={1}>
                        {m.email}
                      </Text>
                    </Box>

                    <Flex gap="10px" alignItems="center">
                      <Button
                        variant="ghost"
                        p="0"
                        h="auto"
                        minW="auto"
                        _hover={{ bg: "transparent" }}
                        _active={{ bg: "transparent" }}
                        onClick={() =>
                          onRemoveMember({ id: m.id, name: m.name })
                        }
                        disabled={
                          isRemovingTeammember && removingMemberId === m.id
                        }
                      >
                        {isRemovingTeammember && removingMemberId === m.id ? (
                          <Skeleton borderRadius="6px" w="20px" h="20px">
                            <AppSpinner w="20px" h="20px" />
                          </Skeleton>
                        ) : (
                          <Image src={deleteIcon} alt="" w="20px" h="20px" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        p="0"
                        h="auto"
                        minW="auto"
                        _hover={{ bg: "transparent" }}
                        _active={{ bg: "transparent" }}
                        onClick={() => onOpenEdit(m)}
                        disabled={
                          isRemovingTeammember && removingMemberId === m.id
                        }
                      >
                        <IconPencil w="20px" h="20px" />
                      </Button>
                    </Flex>
                  </Flex>

                  <Flex mt="10px" gap="8px" flexWrap="wrap">
                    <Box
                      display="inline-block"
                      px="10px"
                      py="4px"
                      borderRadius="999px"
                      bg="#E0E9FF"
                      color="#0B0E10"
                      fontSize="12px"
                      fontWeight="400"
                    >
                      {m.role}
                    </Box>

                    <Box
                      display="inline-block"
                      px="10px"
                      py="4px"
                      borderRadius="999px"
                      bg={m.status === "pending" ? "#FEF3C7" : "#EBFFF1"}
                      color={m.status === "pending" ? "#B45309" : "#0B0E10"}
                      fontSize="12px"
                      fontWeight="400"
                      textTransform="capitalize"
                    >
                      {m.status}
                    </Box>

                    <Box
                      display="inline-block"
                      px="10px"
                      py="4px"
                      borderRadius="999px"
                      bg="#F3F4F6"
                      color="#0B0E10"
                      fontSize="12px"
                      fontWeight="400"
                    >
                      Billing: {m.billingAccess ? "Yes" : "No"}
                    </Box>
                  </Flex>
                </Box>
              ))}
            </Box>
          ) : (
            members.map((m: any, idx: number) => (
              <Flex
                key={m.id}
                px="16px"
                py="12px"
                borderBottom={
                  idx === members.length - 1 ? "none" : "1px solid #E5E7EB"
                }
                alignItems="center"
                fontSize="14px"
                color="#0B0E10 "
              >
                <Box flex="2" wordBreak="break-all">
                  {m.name}
                </Box>

                <Box flex="1">
                  <Box
                    display="inline-block"
                    px="5px"
                    py="4px"
                    borderRadius="8px"
                    bg="#E0E9FF"
                    color="#0B0E10"
                    fontSize="12px"
                    fontWeight="400"
                  >
                    {m.role}
                  </Box>
                </Box>

                <Box flex="3" color="#0B0E10">
                  {m.email}
                </Box>

                <Box flex="1">
                  <Box
                    display="inline-block"
                    px="10px"
                    py="4px"
                    borderRadius="8px"
                    bg={m.status === "pending" ? "#FEF3C7" : "#EBFFF1"}
                    color={m.status === "pending" ? "#B45309" : "#0B0E10"}
                    fontSize="12px"
                    fontWeight="400"
                    textTransform="capitalize"
                  >
                    {m.status}
                  </Box>
                </Box>

                <Box flex="1">{m.billingAccess ? "Yes" : "No"}</Box>

                <Flex flex="1" pl="8px" justifyContent="flex-start" gap="10px">
                  <Button
                    variant="ghost"
                    p="0"
                    h="auto"
                    minW="auto"
                    _hover={{ bg: "transparent" }}
                    _active={{ bg: "transparent" }}
                    onClick={() => onRemoveMember({ id: m.id, name: m.name })}
                    disabled={isRemovingTeammember && removingMemberId === m.id}
                  >
                    {isRemovingTeammember && removingMemberId === m.id ? (
                      <Skeleton borderRadius="6px" w="20px" h="20px">
                        <AppSpinner w="20px" h="20px" />
                      </Skeleton>
                    ) : (
                      <Image src={deleteIcon} alt="" w="20px" h="20px" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    p="0"
                    h="auto"
                    minW="auto"
                    _hover={{ bg: "transparent" }}
                    _active={{ bg: "transparent" }}
                    onClick={() => onOpenEdit(m)}
                    disabled={isRemovingTeammember && removingMemberId === m.id}
                  >
                    <IconPencil w="20px" h="20px" />
                  </Button>
                </Flex>
              </Flex>
            ))
          )}
        </Box>
      </Box>

      <Modal
        isOpen={isOpen}
        onClose={() => {
          onClose();
        }}
        isCentered
      >
        <ModalOverlay bg="rgba(0,0,0,0.4)" />
        <ModalContent borderRadius="18px" maxW="548px" p="32px">
          <ModalHeader
            p="0px"
            m="0px 0px 24px 0px"
            fontSize="24px"
            fontWeight="600"
            color="#0B0E10"
          >
            Invite a new team member
          </ModalHeader>
          <ModalCloseButton
            mt="10px"
            mr="10px"
            _hover={{ bg: "transparent" }}
            _active={{ bg: "transparent" }}
            _focus={{ bg: "transparent" }}
          />
          <ModalBody p="0px">
            <Flex
              mb="16px"
              gap="16px"
              flexDirection={{ base: "column", md: "row" }}
            >
              <Box flex="1">
                <FormLabel
                  fontSize="14px"
                  fontWeight="400"
                  color="#0B0E10"
                  mb="8px"
                >
                  First name
                </FormLabel>
                <AuthInput
                  role="customer"
                  chakraVariant="authCustomerNew"
                  placeholder="Enter first name"
                  value={inviteFirstName}
                  onChange={(e) => setInviteFirstName(e.target.value)}
                  fontSize="16px"
                />
              </Box>

              <Box flex="1">
                <FormLabel
                  fontSize="14px"
                  fontWeight="400"
                  color="#0B0E10"
                  mb="8px"
                >
                  Last name
                </FormLabel>
                <AuthInput
                  role="customer"
                  chakraVariant="authCustomerNew"
                  placeholder="Enter last name"
                  value={inviteLastName}
                  onChange={(e) => setInviteLastName(e.target.value)}
                  fontSize="16px"
                />
              </Box>
            </Flex>

            <Box mb="16px">
              <FormLabel
                fontSize="14px"
                fontWeight="400"
                color="#0B0E10"
                mb="8px"
              >
                Email
              </FormLabel>
              <AuthInput
                role="customer"
                chakraVariant="authCustomerNew"
                placeholder="Enter email address"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                fontSize="16px"
              />
            </Box>

            <Box mb="8px">
              <FormLabel
                fontSize="14px"
                fontWeight="400"
                color="#0B0E10"
                mb="8px"
              >
                Role
              </FormLabel>
              <Menu>
                <MenuButton
                  as={Button}
                  w="full"
                  h="48px"
                  borderRadius="12px"
                  border="1px solid #CBD5E1"
                  bg="#FFF"
                  fontWeight="400"
                  textAlign="left"
                  _hover={{ bg: "#FFF" }}
                  _focus={{ bg: "#FFF", border: "1px solid #3A7BFF" }}
                  _active={{ bg: "#FFF", border: "1px solid #3A7BFF" }}
                  rightIcon={<IconArrowDown width="24px" height="24px" />}
                >
                  {inviteRole === "admin" ? "Admin" : "User"}
                </MenuButton>
                <MenuList
                  boxShadow={shadow}
                  mt="10px"
                  borderRadius="12px"
                  bg="#FFF"
                  border="1px solid #E5E7EB"
                >
                  <Flex
                    flexDirection="column"
                    p="10px 12px"
                    fontSize="14px"
                    lineHeight="24px"
                    fontWeight="400"
                    color="black"
                  >
                    <MenuItem
                      _hover={{ bg: "#F3F4F6" }}
                      _focus={{ bg: "#F3F4F6" }}
                      borderRadius="8px"
                      padding="10px 12px"
                      onClick={() => setInviteRole("admin")}
                    >
                      <Text fontSize="md">Admin</Text>
                    </MenuItem>
                    <MenuItem
                      _hover={{ bg: "#F3F4F6" }}
                      _focus={{ bg: "#F3F4F6" }}
                      borderRadius="8px"
                      padding="10px 12px"
                      onClick={() => setInviteRole("standard")}
                    >
                      <Text fontSize="md">User</Text>
                    </MenuItem>
                  </Flex>
                </MenuList>
              </Menu>
              <Text fontSize="12px" color="#000000" mt="8px" mb="24px">
                Admins can manage billing and team access. Members can place and
                manage orders.
              </Text>
            </Box>
          </ModalBody>

          <ModalFooter p="0px" gap="24px">
            <Button
              bgColor="#FFFFFF"
              color="#3A7BFF"
              h="48px"
              w="230px"
              border="1px solid #3A7BFF"
              _hover={{
                bgColor: "#3A7BFF",
                color: "#FFF",
              }}
              _active={{
                bgColor: "#FFFFFF",
                color: "#3A7BFF",
              }}
              onClick={() => {
                onClose();
              }}
              borderRadius="25px"
            >
              Cancel
            </Button>
            <Button
              bgColor="#3A7BFF"
              color="white"
              h="48px"
              w="230px"
              border="1px solid #3A7BFF"
              _hover={{
                bgColor: "#FFFFFF",
                color: "#3A7BFF",
              }}
              _active={{
                bgColor: "#FFFFFF",
                color: "#3A7BFF",
              }}
              _loading={{ pointerEvents: "none" }}
              onClick={() => onSubmitInvite()}
              isLoading={isSavingTeammember}
              disabled={
                !inviteEmail ||
                !inviteRole ||
                (!inviteFirstName.trim() && !inviteLastName.trim())
              }
              borderRadius="25px"
            >
              Send invite
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isEditOpen}
        onClose={() => {
          onEditClose();
        }}
        isCentered
      >
        <ModalOverlay bg="rgba(0,0,0,0.4)" />
        <ModalContent borderRadius="18px" maxW="548px" p="32px">
          <ModalHeader
            p="0px"
            m="0px 0px 24px 0px"
            fontSize="24px"
            fontWeight="600"
            color="#0B0E10"
          >
            Edit team member
          </ModalHeader>
          <ModalCloseButton
            mt="10px"
            mr="10px"
            _hover={{ bg: "transparent" }}
            _active={{ bg: "transparent" }}
            _focus={{ bg: "transparent" }}
          />
          <ModalBody p="0px" mb="24px">
            <Box mb="8px">
              <FormLabel
                fontSize="14px"
                fontWeight="400"
                color="#0B0E10"
                mb="8px"
              >
                Role
              </FormLabel>
              <Menu>
                <MenuButton
                  as={Button}
                  w="full"
                  h="48px"
                  borderRadius="12px"
                  border="1px solid #CBD5E1"
                  bg="#FFF"
                  fontWeight="400"
                  textAlign="left"
                  _hover={{ bg: "#FFF" }}
                  _focus={{ bg: "#FFF", border: "1px solid #3A7BFF" }}
                  _active={{ bg: "#FFF", border: "1px solid #3A7BFF" }}
                  rightIcon={<IconArrowDown width="24px" height="24px" />}
                >
                  {editRole === "admin" ? "Admin" : "User"}
                </MenuButton>
                <MenuList
                  boxShadow={shadow}
                  mt="10px"
                  borderRadius="12px"
                  bg="#FFF"
                  border="1px solid #E5E7EB"
                >
                  <Flex
                    flexDirection="column"
                    p="10px 12px"
                    fontSize="14px"
                    lineHeight="24px"
                    fontWeight="400"
                    color="black"
                  >
                    <MenuItem
                      _hover={{ bg: "#F3F4F6" }}
                      _focus={{ bg: "#F3F4F6" }}
                      borderRadius="8px"
                      padding="10px 12px"
                      onClick={() => setEditRole("admin")}
                    >
                      <Text fontSize="sm">Admin</Text>
                    </MenuItem>
                    <MenuItem
                      _hover={{ bg: "#F3F4F6" }}
                      _focus={{ bg: "#F3F4F6" }}
                      borderRadius="8px"
                      padding="10px 12px"
                      onClick={() => setEditRole("standard")}
                    >
                      <Text fontSize="sm">User</Text>
                    </MenuItem>
                  </Flex>
                </MenuList>
              </Menu>
            </Box>
          </ModalBody>

          <ModalFooter p="0px" gap="24px">
            <Button
              bgColor="#FFFFFF"
              color="#3A7BFF"
              h="48px"
              w="230px"
              border="1px solid #3A7BFF"
              _hover={{
                bgColor: "#3A7BFF",
                color: "#FFF",
              }}
              _active={{
                bgColor: "#FFFFFF",
                color: "#3A7BFF",
              }}
              borderRadius="25px"
              onClick={() => {
                onEditClose();
              }}
            >
              Cancel
            </Button>
            <Button
              bgColor="#3A7BFF"
              color="white"
              h="48px"
              w="230px"
              border="1px solid #3A7BFF"
              _hover={{
                bgColor: "#FFFFFF",
                color: "#3A7BFF",
              }}
              _active={{
                bgColor: "#FFFFFF",
                color: "#3A7BFF",
              }}
              onClick={() => onSubmitEdit()}
              isLoading={isChangingTeammemberRole}
              disabled={!editMember?.id || isChangingTeammemberRole}
              borderRadius="25px"
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered>
        <ModalOverlay bg="rgba(0,0,0,0.4)" />
        <ModalContent borderRadius="12px" maxW="400px" p="24px">
          <ModalCloseButton _hover={{}} />
          <ModalHeader p="0" mb="16px">
            <Text fontSize="18=6px" fontWeight="600" color="#0B0E10">
              Remove team member
            </Text>
          </ModalHeader>
          <ModalBody p="0" mb="24px">
            <Text fontSize="14px" color="#4B5563">
              Are you sure you want to remove{" "}
              {memberToDelete?.name || "this member"} from your team?
            </Text>
          </ModalBody>
          <ModalFooter p="0" gap="12px">
            <Button
              variant="outline"
              borderRadius="12px"
              h="40px"
              px="20px"
              fontSize="14px"
              fontWeight="600"
              color="#155EEF"
              borderColor="#155EEF"
              _hover={{ bg: "#fff" }}
              onClick={onDeleteClose}
            >
              Cancel
            </Button>
            <Button
              bgColor="#DC2626"
              color="white"
              borderRadius="12px"
              h="40px"
              px="20px"
              fontSize="14px"
              fontWeight="600"
              border="1px solid #DC2626"
              _hover={{ bgColor: "#fff", color: "#DC2626" }}
              onClick={onConfirmDelete}
              isLoading={isRemovingTeammember}
            >
              Remove
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
